import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, InputLabel } from '@mui/material';
import { FieldError } from './FieldError';

export const withFormControl = (Component) => {
  const WrappedComponent = forwardRef(({ id, name, label, control, size = 'small', ...rest }, ref) => {
    const { formState } = useFormContext() || {}; // Ensure it doesn't break outside FormProvider
    const error = name.split('.').reduce((acc, part) => acc?.[part], formState?.errors) || ''; // Handle nested errors

    return (
      <FormControl
        fullWidth
        size={size}
        error={Boolean(error)}
      >
        {label && <InputLabel id={id}>{label}</InputLabel>}
        <Controller
          name={name}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Component
              {...field}
              {...rest}
              ref={ref}
              label={label}
              error={Boolean(error)}
            />
          )}
        />
        {error && <FieldError message={error.message} />}
      </FormControl>
    );
  });

  WrappedComponent.displayName = `WithFormField(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

export const withFieldError = (Component) => {
  const WrappedComponent = forwardRef(({ name, label, control, rules, ...rest }, ref) => {
    const { formState } = useFormContext() || {}; // Ensure it doesn't break outside FormProvider
    const error = name.split('.').reduce((acc, part) => acc?.[part], formState?.errors) || ''; // Handle nested errors

    return (
      <div>
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <Component
              {...field}
              {...rest}
              ref={ref}
              label={label}
              error={Boolean(error)}
            />
          )}
        />
        {error && <FieldError message={error.message} />}
      </div>
    );
  });

  WrappedComponent.displayName = `withFieldError(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
};

export const withFieldArrayError = (WrappedComponent) => {
  const EnhancedComponent = ({ name, ...props }) => {
    const {
      formState: { errors },
    } = useFormContext();

    const rootError = errors?.[name]?.message || errors?.[name]?.root?.message;

    return (
      <>
        <WrappedComponent
          {...props}
          name={name}
        />
        {rootError && <FieldError message={rootError} />}
      </>
    );
  };

  return EnhancedComponent;
};
